import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { OnboardingData, MockResults, generateMockResults } from "./businessData";
import type { SelectedNeighborhood } from "./neighborhoodRent";
import { EXPANDED_NEIGHBORHOODS } from "./neighborhoodData";

interface AppContextType {
  onboarding: OnboardingData;
  setOnboarding: React.Dispatch<React.SetStateAction<OnboardingData>>;
  results: MockResults | null;
  generateResults: () => void;
  /**
   * Always returns a usable MockResults object: real `results` if the user
   * has run onboarding, otherwise a synthetic preview generated from any
   * partial onboarding data (filled in with sensible defaults). This lets
   * feature pages render for paid users who returned from Stripe Checkout
   * before completing onboarding, instead of bouncing them back.
   */
  ensureResults: () => MockResults;
  /** True when `results` came from real onboarding (vs. synthetic preview). */
  hasRealResults: boolean;
  beginnerMode: boolean;
  setBeginnerMode: React.Dispatch<React.SetStateAction<boolean>>;
  manuallyOpened: boolean;
  setManuallyOpened: (v: boolean) => void;
  isOperationsUnlocked: boolean;
  selectedNeighborhood: SelectedNeighborhood | null;
  setSelectedNeighborhood: (n: SelectedNeighborhood | null) => void;
}

const defaultOnboarding: OnboardingData = {
  city: "",
  businessType: "",
  businessName: "",
  goal: "",
  selectedPlan: "",
  budget: "",
  hasPartners: false,
  experience: "",
  businessSize: "",
  locationType: "",
  targetCustomer: "",
  launchSpeed: "",
  targetOpeningDate: "",
  sqftMin: 0,
  sqftMax: 0,
  seats: 0,
  seatingType: "",
  serviceStyle: "",
  openingHours: "",
  daysPerWeek: 7,
  teamSize: "",
  alcoholType: "none",
  spacePreference: "",
  outlets: [],
  indoorSeats: 0,
  outdoorSeats: 0,
  barSeats: 0,
  guestRooms: [],
  suitesCount: 0,
  meetingRoomsCapacity: 0,
  guestsPerEvent: 0,
  eventsPerMonth: 0,
  kitchenLocation: "",
  maxCapacityPerEvent: 0,
  counterSeats: 0,
  avgDailyCovers: 0,
  monthsOpen: "",
  monthlyRevenueEstimate: 0,
  biggestChallenge: "",
  country: "",
};

const RESULTS_KEY = "oplif_results";
const ONBOARDING_KEY = "oplif_onboarding";
const MANUALLY_OPENED_KEY = "oplif_manually_opened";
const SELECTED_NEIGHBORHOOD_KEY = "oplif_selected_neighborhood";

function loadPersistedSelectedNeighborhood(): SelectedNeighborhood | null {
  try {
    // Prefer localStorage (survives the Stripe Checkout round-trip), but
    // fall back to any value previously written to sessionStorage so existing
    // sessions don't lose their selection.
    const raw =
      localStorage.getItem(SELECTED_NEIGHBORHOOD_KEY) ??
      sessionStorage.getItem(SELECTED_NEIGHBORHOOD_KEY);
    if (raw) return JSON.parse(raw) as SelectedNeighborhood;
  } catch {}
  return null;
}

function loadPersistedResults(): MockResults | null {
  try {
    const raw =
      localStorage.getItem(RESULTS_KEY) ??
      sessionStorage.getItem(RESULTS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return null;
}

function loadPersistedOnboarding(): OnboardingData {
  try {
    const raw =
      localStorage.getItem(ONBOARDING_KEY) ??
      sessionStorage.getItem(ONBOARDING_KEY);
    if (raw) return { ...defaultOnboarding, ...JSON.parse(raw) };
  } catch {}
  return defaultOnboarding;
}

function loadPersistedManuallyOpened(): boolean {
  try {
    return localStorage.getItem(MANUALLY_OPENED_KEY) === "true";
  } catch {
    return false;
  }
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [onboarding, setOnboarding] = useState<OnboardingData>(loadPersistedOnboarding);
  const [results, setResults] = useState<MockResults | null>(loadPersistedResults);
  const [beginnerMode, setBeginnerMode] = useState(true);
  const [manuallyOpened, setManuallyOpenedState] = useState<boolean>(loadPersistedManuallyOpened);
  const [selectedNeighborhood, setSelectedNeighborhoodState] = useState<SelectedNeighborhood | null>(loadPersistedSelectedNeighborhood);

  // Persist onboarding data
  useEffect(() => {
    try {
      // Use localStorage so the value persists across the Stripe Checkout
      // redirect (sessionStorage is per top-level browsing context and gets
      // wiped on some browsers when returning from a third-party domain).
      localStorage.setItem(ONBOARDING_KEY, JSON.stringify(onboarding));
    } catch {}
  }, [onboarding]);

  // Track the last *committed* city key (i.e., one that matches the saved
  // neighborhood selection's city). Only clear the selection when the user
  // genuinely switches to a different city — not while they're mid-typing or
  // re-onboarding into the same city.
  const lastCommittedCityRef = useRef<string | null>(
    selectedNeighborhood?.cityKey ?? null,
  );

  useEffect(() => {
    if (!selectedNeighborhood) {
      // Nothing to protect; reset baseline so a future selection isn't
      // immediately invalidated.
      lastCommittedCityRef.current = null;
      return;
    }
    const currentKey = onboarding.city.toLowerCase().trim();
    const selectedKey = selectedNeighborhood.cityKey;

    // Same city as the selection — re-anchor the baseline and keep selection.
    if (currentKey === selectedKey) {
      lastCommittedCityRef.current = selectedKey;
      return;
    }

    // Empty / partial city input — do NOT clear the selection. We only act on
    // committed cities that exist in our neighborhood dataset.
    if (!currentKey) return;
    const isKnownCity = Object.prototype.hasOwnProperty.call(EXPANDED_NEIGHBORHOODS, currentKey);
    if (!isKnownCity) return;

    // Committed, known city that differs from the selection — clear it.
    if (currentKey !== selectedKey) {
      setSelectedNeighborhoodState(null);
      try {
        localStorage.removeItem(SELECTED_NEIGHBORHOOD_KEY);
        sessionStorage.removeItem(SELECTED_NEIGHBORHOOD_KEY);
      } catch {}
      lastCommittedCityRef.current = currentKey;
    }
  }, [onboarding.city, selectedNeighborhood]);

  const setManuallyOpened = (v: boolean) => {
    setManuallyOpenedState(v);
    try {
      localStorage.setItem(MANUALLY_OPENED_KEY, v ? "true" : "false");
    } catch {}
  };

  const setSelectedNeighborhood = (n: SelectedNeighborhood | null) => {
    setSelectedNeighborhoodState(n);
    try {
      if (n) {
        localStorage.setItem(SELECTED_NEIGHBORHOOD_KEY, JSON.stringify(n));
      } else {
        localStorage.removeItem(SELECTED_NEIGHBORHOOD_KEY);
        sessionStorage.removeItem(SELECTED_NEIGHBORHOOD_KEY);
      }
    } catch {}
  };

  // Auto-unlock when today >= targetOpeningDate, plus manual override
  const autoUnlocked = (() => {
    const d = onboarding.targetOpeningDate;
    if (!d) return false;
    const opening = new Date(d);
    if (isNaN(opening.getTime())) return false;
    return new Date() >= opening;
  })();
  const isOperationsUnlocked = manuallyOpened || autoUnlocked;

  const generateResults = () => {
    const newResults = generateMockResults(onboarding);
    setResults(newResults);
    try {
      localStorage.setItem(RESULTS_KEY, JSON.stringify(newResults));
    } catch {}
  };

  // Synthetic results for paid users who haven't (or can't) complete onboarding.
  // Mirrors the sample-fallback approach used by Dashboard so feature pages
  // never need to bounce the user away. Not persisted.
  const ensureResults = (): MockResults => {
    if (results) return results;
    const sample: OnboardingData = {
      ...onboarding,
      businessName: onboarding.businessName || "My Business",
      businessType: onboarding.businessType || "restaurant",
      city: onboarding.city || "miami",
      budget: onboarding.budget || "100k-250k",
      experience: onboarding.experience || "some",
      businessSize: onboarding.businessSize || "medium",
      locationType: onboarding.locationType || "permanent",
      launchSpeed: onboarding.launchSpeed || "balanced",
      serviceStyle: onboarding.serviceStyle || "full-service",
      teamSize: onboarding.teamSize || "full",
      seats: onboarding.seats || 60,
      indoorSeats: onboarding.indoorSeats || 40,
      outdoorSeats: onboarding.outdoorSeats || 20,
      barSeats: onboarding.barSeats || 0,
      sqftMin: onboarding.sqftMin || 1500,
      sqftMax: onboarding.sqftMax || 2500,
      daysPerWeek: onboarding.daysPerWeek || 7,
      goal: onboarding.goal || "opening",
    };
    return generateMockResults(sample);
  };
  const hasRealResults = !!results;

  return (
    <AppContext.Provider value={{
      onboarding, setOnboarding, results, generateResults,
      ensureResults, hasRealResults,
      beginnerMode, setBeginnerMode,
      manuallyOpened, setManuallyOpened, isOperationsUnlocked,
      selectedNeighborhood, setSelectedNeighborhood,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useAppContext() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useAppContext must be used within AppProvider");
  return ctx;
}
